import { CommonModule } from "@angular/common";
import { Component, NgModule, Output, EventEmitter, ElementRef, AfterViewInit, OnDestroy, Input, HostBinding } from "@angular/core";
import * as events from "devextreme/events";
import { AppStateManageService } from "../../../services/app-state-manage.service";
import { NavigationEnd, Router } from "@angular/router";
import { SidebarMenuStateService } from "../../../services/sidebarmenustateservice";
import { Subscription } from "rxjs";
import { menuMetaConfig, SUBMENU_ID, UPDATED_REPORTS_FEATURE } from "../../../classes/domain/constants";
import { TranslationHelperService } from "../../../services/translationhelper.service";
import { environment } from "@environments/environment";

export interface NavItem_Entity_Authority {
  Feature: string;
  FeatureGroup: string;
  MenuId: string;
  CanAdd?: boolean;
  CanView: boolean;
  CanEdit?: boolean;
  CanDelete?: boolean;
  CanExport?: boolean;
}

@Component({
  selector: "app-side-navigation-menu",
  templateUrl: "./side-navigation-menu.component.html",
  styleUrls: ["./side-navigation-menu.component.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class SideNavigationMenuComponent implements AfterViewInit, OnDestroy {

  @Output() openMenu = new EventEmitter<any>();
  @Output() selectedItemChanged = new EventEmitter<any>();

  SideBarMenuItemList: NavItem_Entity_Authority[] = [];
  public items: any[] = [];
  activeFeature: string = '';
  private menuSubscription!: Subscription;
  private langSubscription!: Subscription;
  private routerSubscription!: Subscription;

  // Widths now come from the parent (which reacts to ScreenService),
  // so the rail width always matches dx-drawer's minSize and never clips.
  @Input() expandedWidth = 250;
  @Input() collapsedWidth = 60;
  skipdashboard: boolean = environment.skipDashboard;
  isCompact = false;

  // Replaces the old fixed `width: 250px !important` in SCSS.
  // !important in a stylesheet still beats an inline style, so the SCSS
  // must NOT set width anymore — this binding is now the single source of truth.
  @HostBinding('style.width.px')
  get hostWidth(): number {
    return this.isCompact ? this.collapsedWidth : this.expandedWidth;
  }

  @HostBinding('class.compact')
  get compactClass(): boolean {
    return this.isCompact;
  }

  // Fixed items that always show at the top, each with its own route
  private readonly pinnedItems = [
    { feature: 'Dashboard', route: ['dashboard'] },
    { feature: UPDATED_REPORTS_FEATURE, route: [SUBMENU_ID.FEATURE_GROUP, UPDATED_REPORTS_FEATURE] },
  ];
  private readonly pinnedFeatureNames = this.pinnedItems.map(p => p.feature);

  private featureIconMap: Record<string, { icon: string; selectedIcon: string }> = {
    Dashboard: { icon: '/assets/icons/dashboard.png', selectedIcon: '/assets/icons/filled_dashboard_pink.png' },
    Master: { icon: '/assets/icons/Master-Feature.png', selectedIcon: '/assets/icons/filled_masters_pink.png' },
    Transaction: { icon: '/assets/icons/Transaction-Feature.png', selectedIcon: '/assets/icons/filled_transaction_pink.png' },
    Reports: { icon: '/assets/icons/Reports-Feature.png', selectedIcon: '/assets/icons/filled_report_pink.png' },
    [UPDATED_REPORTS_FEATURE]: { icon: '/assets/icons/Reports-Feature.png', selectedIcon: '/assets/icons/filled_report_pink.png'},
  };

  private defaultIcon = {
    icon: '/assets/icons/masters.png',
    selectedIcon: '/assets/icons/masters_active.png'
  };

  constructor(
    private elementRef: ElementRef,
    private sidebarMenuState: SidebarMenuStateService,
    private router: Router,
    private appStateManage: AppStateManageService,
    private translationHelper: TranslationHelperService,
  ) { }

  onItemClick2(item: any, event?: Event) {
    event?.stopPropagation();
    const featureName = item?.featureName || '';
    this.activeFeature = featureName;

    this.selectedItemChanged.emit({ itemData: item });

    // if (featureName === 'Dashboard') {
    //   this.router.navigate(['home']);
    //   return;
    // }

    const pinned = this.pinnedItems.find(p => p.feature === featureName);
    if (pinned) {
      this.router.navigate(pinned.route);
      return;
    }
    this.router.navigate([SUBMENU_ID.FEATURE_GROUP, featureName]);
  }

  @Input() set selectedItem(value: string) {
    if (value) {
      this.setActiveFromUrl(value);
    }
  }

  @Input() set compactMode(value: boolean) {
    this.isCompact = value;
  }

  ngAfterViewInit() {
    events.on(this.elementRef.nativeElement, "dxclick", (e: Event) =>
      this.openMenu.next(e)
    );
  }

  async ngOnInit() {
    const LoginToken = this.appStateManage.StorageKey.getItem('LoginToken');
    if (!LoginToken) return;

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.setActiveFromUrl(event.urlAfterRedirects);
      }
    });
    this.setActiveFromUrl(this.router.url);

    this.langSubscription = this.translationHelper.whenLangReady()
      .subscribe(() => {
        this.GenerateAndSetMenuItemModuleList();
      });

    this.menuSubscription = this.sidebarMenuState.menu$
      .subscribe(async (menu: NavItem_Entity_Authority[]) => {
        this.SideBarMenuItemList = menu;
        await this.GenerateAndSetMenuItemModuleList();
        this.setActiveFromUrl(this.router.url);
      });
  }

  private setActiveFromUrl(url: string) {
    if (!url) return;

    const cleanUrl = url
      .replace('/#/', '')
      .replace('#/', '')
      .replace(/^\//, '');

    const pathOnly = cleanUrl.split('?')[0].split('#')[0];

    // 1. Feature Group pattern (/Feature-Group/:feature)
    const match = cleanUrl.match(/Feature-Group\/([^?#]+)/);
    if (match) {
      const groupFeature = decodeURIComponent(match[1] || '');
      this.setFeatureIfValid(groupFeature);
      return;
    }

    // 1b. Dynamic (common) report pages belong to Updated Reports
    if (pathOnly.startsWith(`${SUBMENU_ID.DYNAMIC_REPORT}/`)) {
      this.setFeatureIfValid(UPDATED_REPORTS_FEATURE);
      return;
    }

    // 2. Dashboard and Today's tasks routes
    if (
      pathOnly === '' ||
      pathOnly === 'dashboard' ||
      pathOnly === 'home' ||
      pathOnly === 'updated-dashboard' ||
      pathOnly === 'old-dashboard' ||
      pathOnly === 'todays-task-list' ||
      pathOnly === 'todays-task-list-details' ||
      pathOnly === 'dashboard-drilldown-report'
    ) {
      this.setFeatureIfValid('Dashboard');
      return;
    }

    // 3. Other pinned items
    const pinned = this.pinnedItems.find(p => p.route.includes(pathOnly));
    if (pinned) {
      this.setFeatureIfValid(pinned.feature);
      return;
    }

    // Get menu items (from instance list or sessionStorage fallback)
    let menuList: NavItem_Entity_Authority[] = this.SideBarMenuItemList;
    if (!menuList || menuList.length === 0) {
      try {
        menuList = JSON.parse(sessionStorage.getItem('ValidMenuItemIds2') || '[]');
      } catch {
        menuList = [];
      }
    }

    // 4. Resolve via menuMetaConfig
    const matchedEntry = Object.entries(menuMetaConfig).find(
      ([_, val]) => val.path.toLowerCase() === pathOnly.toLowerCase()
    );

    if (matchedEntry) {
      const [menuId] = matchedEntry;
      const menuItem = menuList.find(m => m.MenuId.toLowerCase() === menuId.toLowerCase());
      if (menuItem?.Feature) {
        this.setFeatureIfValid(menuItem.Feature);
        return;
      }
    }

    // 5. Direct MenuId match in menuList
    const directMenuItem = menuList.find(
      m => m.MenuId.toLowerCase() === pathOnly.toLowerCase()
    );
    if (directMenuItem?.Feature) {
      this.setFeatureIfValid(directMenuItem.Feature);
      return;
    }

    // 6. Detail / add / edit pages (e.g. *_details, *_add_details, etc.)
    const detailSuffixes = ['_add_details', '_edit_details', '_form_details', '_details', '_detail']
      .sort((a, b) => b.length - a.length);

    let parentUrl = pathOnly;
    const lowerPath = pathOnly.toLowerCase();
    for (const suffix of detailSuffixes) {
      if (lowerPath.endsWith(suffix)) {
        parentUrl = pathOnly.slice(0, pathOnly.length - suffix.length);
        break;
      }
    }

    const parentEntry = Object.entries(menuMetaConfig).find(
      ([_, val]) => val.path.toLowerCase() === parentUrl.toLowerCase()
    );
    if (parentEntry) {
      const [menuId] = parentEntry;
      const menuItem = menuList.find(m => m.MenuId.toLowerCase() === menuId.toLowerCase());
      if (menuItem?.Feature) {
        this.setFeatureIfValid(menuItem.Feature);
        return;
      }
    }

    // 7. Fallback check: check if path contains any menu item
    for (const item of menuList) {
      if (item.MenuId && pathOnly.toLowerCase().includes(item.MenuId.toLowerCase())) {
        this.setFeatureIfValid(item.Feature);
        return;
      }
    }
  }

  private setFeatureIfValid(featureName: string) {
    if (!featureName) return;

    const match = this.items.find(
      (it) => it.featureName.toLowerCase() === featureName.toLowerCase()
    );

    this.activeFeature = match ? match.featureName : featureName;
  }

  // private GenerateAndSetMenuItemModuleList = async () => {
  //   const features = Array.from(new Set(this.SideBarMenuItemList.map(m => m.Feature)));
  //   this.items = features.map(feature => {
  //     const icons = this.featureIconMap[feature] || this.defaultIcon;
  //     return {
  //       text: this.translationHelper.getFeature(feature),
  //       featureName: feature,
  //       icon: icons.icon,
  //       selectedIcon: icons.selectedIcon,
  //     };
  //   });
  // };


  private GenerateAndSetMenuItemModuleList = async () => {
    const serverFeatures = Array.from(
      new Set(this.SideBarMenuItemList.map(m => m.Feature))
    ).filter(f => !this.pinnedFeatureNames.includes(f));

    const features = [...this.pinnedFeatureNames, ...serverFeatures];

    this.items = features.map(feature => {
      const icons = this.featureIconMap[feature] || this.defaultIcon;
      return {
        text: this.translationHelper.getFeature(feature) || feature,
        featureName: feature,
        icon: icons.icon,
        selectedIcon: icons.selectedIcon,
      };
    });
  };

  ngOnDestroy() {
    events.off(this.elementRef.nativeElement, "dxclick");
    if (this.menuSubscription) {
      this.menuSubscription.unsubscribe();
      this.langSubscription?.unsubscribe();
      this.routerSubscription?.unsubscribe();
    }
  }
}

@NgModule({
  imports: [SideNavigationMenuComponent],
  exports: [SideNavigationMenuComponent],
})
export class SideNavigationMenuModule { }